import {Card, DataStatus, Label, Separator, Tooltip} from 'platform/components';
import {
  Box,
  Center,
  Grid,
  Heading,
  HStack,
  Icon,
  Text,
  ThemeColorTextPath,
  VStack,
} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match, Pattern} from 'ts-pattern';

import {useCallback} from 'react';

import {always, isNotEmpty, isNotNil, mergeAll} from 'ramda';
import {isNegative, isPositive} from 'ramda-adjunct';

import {useGetArticleStockAvailabilityOverviewQuery} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {DmsDataGrid, Section} from '@dms/shared';
import {getQuantityWithHandlingUnit} from '@dms/warehouse/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {parseDate, suffixTestId, TestIdProps} from 'shared/web';

import type {EagGridMaximization, QueryFilterObject} from 'features/eag-grid';

type AvailableTextColor = Extract<ThemeColorTextPath, 'primary' | 'danger' | 'success'>;

interface AvailabilityProps extends TestIdProps {
  maximization?: EagGridMaximization;
  handlingUnitLabel: string;
}

export function Availability(props: AvailabilityProps) {
  const params = useWarehouseParams();
  const dateFormatter = useDateTimeFormatter();

  const {
    data: articleAvailabilityOverview,
    isLoading: isArticleAvailabilityOverviewLoading,
    isError: isArticleAvailabilityOverviewError,
  } = useGetArticleStockAvailabilityOverviewQuery(
    {
      warehouseId: params.warehouseId,
      articleId: params.articleId,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const isLoading = isArticleAvailabilityOverviewLoading;
  const isError = isArticleAvailabilityOverviewError;

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => mergeAll([filter, {articleId: params.articleId}]),
    [params]
  );

  if (!articleAvailabilityOverview) {
    return (
      <Center width="100%">
        <DataStatus isLoading={isLoading} isError={isError} minHeight="100vh" />
      </Center>
    );
  }

  const lastOrderDate =
    isNotNil(articleAvailabilityOverview.lastOrder) &&
    isNotEmpty(articleAvailabilityOverview.lastOrder)
      ? dateFormatter('dateShort', parseDate(articleAvailabilityOverview.lastOrder))
      : '-';

  const lastReceptionDate =
    isNotNil(articleAvailabilityOverview.lastReception) &&
    isNotEmpty(articleAvailabilityOverview.lastReception)
      ? dateFormatter('dateShort', parseDate(articleAvailabilityOverview.lastReception))
      : '-';

  const lastDispatchDate =
    isNotNil(articleAvailabilityOverview.lastDispatch) &&
    isNotEmpty(articleAvailabilityOverview.lastDispatch)
      ? dateFormatter('dateShort', parseDate(articleAvailabilityOverview.lastDispatch))
      : '-';

  const availabilityTextColor = match<number, AvailableTextColor>(
    articleAvailabilityOverview.availableQuantity
  )
    .with(Pattern.when(isPositive), always('success'))
    .with(Pattern.when(isNegative), always('danger'))
    .otherwise(always('primary'));

  const transitTooltip = (
    <VStack spacing={4}>
      <Text size="xSmall" color="white">
        {i18n.t('entity.warehouse.labels.transit.description')}
      </Text>
      <VStack>
        <Text size="xSmall" alternative color="white">
          {i18n.t('entity.warehouse.labels.transit.forRequests')}
        </Text>
        <Text size="xSmall" color="white">
          {i18n.t('entity.warehouse.labels.transit.forRequestsDescription')}
        </Text>
      </VStack>
      <VStack>
        <Text size="xSmall" alternative color="white">
          {i18n.t('entity.warehouse.labels.transit.free')}
        </Text>
        <Text size="xSmall" color="white">
          {i18n.t('entity.warehouse.labels.transit.freeDescription')}
        </Text>
      </VStack>
    </VStack>
  );

  const inTransitQuantity = `${articleAvailabilityOverview.inTransitForRequestsQuantity ?? 0} ${
    props.handlingUnitLabel
  } / ${articleAvailabilityOverview.inTransitOnStockQuantity ?? 0} ${props.handlingUnitLabel}`;

  return (
    <Section isFullHeight>
      <VStack spacing={4} height="100%">
        <Card title={i18n.t('entity.warehouse.labels.availabilityOverview')}>
          <VStack>
            <Grid columns={5}>
              <VStack>
                <Label>{i18n.t('entity.warehouse.labels.available')}</Label>
                <Heading color={availabilityTextColor} size={1}>
                  {getQuantityWithHandlingUnit(
                    articleAvailabilityOverview.availableQuantity,
                    props.handlingUnitLabel
                  )}
                </Heading>
              </VStack>
              <VStack>
                <Label>{i18n.t('entity.warehouse.labels.stock')}</Label>
                <Heading size={1}>
                  {getQuantityWithHandlingUnit(
                    articleAvailabilityOverview.stockQuantity,
                    props.handlingUnitLabel
                  )}
                </Heading>
              </VStack>
              <VStack>
                <Label>{i18n.t('entity.warehouse.labels.reserved')}</Label>
                <Heading size={1}>
                  {getQuantityWithHandlingUnit(
                    articleAvailabilityOverview.reservedQuantity,
                    props.handlingUnitLabel
                  )}
                </Heading>
              </VStack>
              <VStack>
                <Label>{i18n.t('entity.warehouse.labels.pendingReturnToSupplier')}</Label>
                <Heading size={1}>
                  {getQuantityWithHandlingUnit(
                    articleAvailabilityOverview.pendingCreditNoteQuantity,
                    props.handlingUnitLabel
                  )}
                </Heading>
              </VStack>
              <VStack>
                <HStack spacing={1}>
                  <Label>{i18n.t('entity.warehouse.labels.inTransit')}</Label>
                  <Tooltip description={transitTooltip}>
                    <Icon value="action/help" color="text.secondary" size={4} />
                  </Tooltip>
                </HStack>
                <Heading size={1}>{inTransitQuantity}</Heading>
              </VStack>
            </Grid>
            <Separator />
            <VStack spacing={4}>
              <Grid columns={5}>
                <VStack>
                  <Label>{i18n.t('entity.warehouse.labels.averageDeliveryTime')}</Label>
                  <Text>TODO</Text>
                </VStack>
                <VStack>
                  <Label>{i18n.t('entity.warehouse.labels.lastOrder')}</Label>
                  <Text>{lastOrderDate}</Text>
                </VStack>
                <VStack>
                  <Label>{i18n.t('entity.warehouse.labels.lastReception')}</Label>
                  <Text>{lastReceptionDate}</Text>
                </VStack>
                <VStack>
                  <Label>{i18n.t('entity.warehouse.labels.lastDispatch')}</Label>
                  <Text>{lastDispatchDate}</Text>
                </VStack>
                <VStack>
                  <Label>{i18n.t('entity.warehouse.labels.minimumStockLevels')}</Label>
                  <Text>TODO</Text>
                </VStack>
              </Grid>
              <Grid columns={5}>
                <VStack>
                  <Label>{i18n.t('entity.warehouse.labels.maximumStockLevels')}</Label>
                  <Text>TODO</Text>
                </VStack>
                <VStack>
                  <Label>{i18n.t('entity.warehouse.labels.stockAnalysis')}</Label>
                  <Text>TODO</Text>
                </VStack>
                <VStack>
                  <Label>{i18n.t('entity.warehouse.labels.fastTrackIssueQuantity')}</Label>
                  <Text>{articleAvailabilityOverview.fastTrackIssueQuantity}</Text>
                </VStack>
              </Grid>
            </VStack>
          </VStack>
        </Card>
        <Card title={i18n.t('entity.warehouse.labels.otherWarehouses')} isFullHeight>
          <Box height="100%">
            <DmsDataGrid
              maximization={props.maximization}
              gridCode="warehouse-other-availability"
              queryModifier={queryModifier}
              data-testid={suffixTestId('otherAvailability', props)}
            />
          </Box>
        </Card>
      </VStack>
    </Section>
  );
}
